<template>
    <component
        :is="forceHardNav ? 'a' : (isInternal ? NuxtLink : 'a')"
        :to="!forceHardNav && isInternal && !disabled ? computedTo : undefined"
        :href="forceHardNav ? hardNavHref : (!isInternal ? computedHref : undefined)"
        :target="target || undefined"
        :rel="target === '_blank' ? 'noopener noreferrer' : undefined"
        :disabled="disabled || undefined"
        :aria-disabled="disabled || undefined"
        @click="handleClick"
    >
        <slot>{{ text }}</slot>
    </component>
</template>

<script setup lang="ts">
import { NuxtLink } from '#components'

export type LinkVariant = 'default' | 'underline'
export type LinkType = 'internal' | 'external'

/** Route object format returned by Shopware helpers like getCategoryRoute */
export type RouteObject = {
    path: string
    state?: Record<string, unknown>
}

export interface MiscLinkProps {
    /** @deprecated Use `href` instead. Path for internal navigation (backwards compatibility) */
    to?: string | RouteObject
    /** Link destination URL or path */
    href?: string
    /** Display text when no slot content provided */
    text?: string
    /** Link target attribute - pass null to not render the attribute */
    target?: '_self' | '_blank' | '_parent' | '_top' | null
    /** Link type - internal uses NuxtLink, external uses anchor tag */
    type?: LinkType
    /** Whether the link is disabled - pass null to not render the attribute */
    disabled?: boolean | null
    /** @deprecated Skip locale prefix for internal links (backwards compatibility) */
    withoutLocale?: boolean
}

const props = withDefaults(defineProps<MiscLinkProps>(), {
    to: undefined,
    href: undefined,
    text: '',
    target: null,
    variant: 'default',
    type: 'internal',
    disabled: null,
    withoutLocale: false,
})

const emit = defineEmits<{
    (e: 'click', event: MouseEvent): void
}>()

const localePath = useLocalePath()
const { formatLink } = useInternationalization(localePath)

// Determine if this is an internal link
const isInternal = computed(() => {
    // If old `to` prop is used, always treat as internal
    if (props.to !== undefined) {
        return true
    }
    return props.type === 'internal'
})

// Extract the path string from to prop (handles both string and object formats)
const toPath = computed(() => {
    if (props.to === undefined) return undefined
    return typeof props.to === 'string' ? props.to : props?.to?.path
})

// Get the actual path/href value (supports both old and new API)
const linkPath = computed(() => {
    return toPath.value ?? props.href ?? '#'
})

// Computed path for internal links with locale handling
const computedTo = computed(() => {
    const path = linkPath.value

    // If withoutLocale is true (backwards compatibility), return raw path or object
    if (props.withoutLocale) {
        // Preserve RouteObject structure if original to was an object
        if (props.to && typeof props.to === 'object') {
            return { path, state: props.to.state }
        }
        return path
    }

    // Use Shopware's formatLink for proper locale handling
    const formattedPath = formatLink(path)

    // Preserve RouteObject structure if original to was an object
    if (props.to && typeof props.to === 'object') {
        return { path: formattedPath, state: props.to.state }
    }

    return formattedPath
})

// Computed href for external links
const computedHref = computed(() => {
    return linkPath.value
})

// Target path as a plain string (computedTo may be a string or a { path } object; only a
// string path is usable for router.resolve / the regex — anything else -> undefined -> no hard nav).
const targetPathStr = computed<string | undefined>(() => {
    const t = computedTo.value
    if (typeof t === 'string') return t
    return typeof t?.path === 'string' ? t.path : undefined
})

const router = useRouter()
const currentRoute = useRoute()

// Shopware technical URLs that always 301 to their canonical SEO URL.
const TECHNICAL_URL_RE = /^(?:\/[a-z]{2})?\/(?:detail|navigation|landingPage)\//

// Force a full document load instead of SPA navigation when the target both
//  (a) is a technical URL that will 301 (deterministic string check), AND
//  (b) would switch the Nuxt layout (target layout !== current layout, read from the route
//      table via router.resolve — no fetch, no guessing).
// Exactly that combination triggers a client-side Suspense mount-commit freeze; a hard load
// sidesteps it (fresh SSR render, redirect handled server-side as a real 301).
const forceHardNav = computed(() => {
    if (!isInternal.value || props.disabled) return false
    const path = targetPathStr.value
    if (path == null || !TECHNICAL_URL_RE.test(path)) return false
    let targetLayout: string
    try {
        targetLayout = (router.resolve(path).meta?.layout as string | undefined) ?? 'default'
    } catch {
        return false
    }
    const currentLayout = (currentRoute.meta?.layout as string | undefined) ?? 'default'
    return targetLayout !== currentLayout
})

// A plain <a href> bypasses vue-router entirely, so — unlike :to on NuxtLink — it never
// gets BASEPATH prepended automatically. Add it ourselves (e.g. /be/detail/xxx, not /detail/xxx).
const baseURL = useRuntimeConfig().app.baseURL
const basePrefix = baseURL && baseURL !== '/' ? baseURL.replace(/\/$/, '') : ''

const hardNavHref = computed(() => {
    const path = targetPathStr.value
    return path != null ? `${basePrefix}${path}` : path
})

const handleClick = (event: MouseEvent) => {
    if (props.disabled) {
        event.preventDefault()
        return
    }
    emit('click', event)
}
</script>
